"use client";

import { useEffect } from "react";
import type { User } from "@supabase/supabase-js";

/** Repair contact delivery on hydration/upgrade; token refresh alone does not republish. */
export function useAuthContactSaved(user: User | null): void {
  const userId = user?.id;
  const anonymous = user?.is_anonymous;
  const updatedAt = user?.updated_at;
  const email = user?.email;
  const phone = user?.phone;
  useEffect(() => {
    if (!userId || anonymous) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function publish(attempt: number): Promise<void> {
      try {
        const response = await fetch("/api/auth/contact-saved", {
          method: "POST",
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(8000)])
        });
        if (response.ok || response.status === 401 || response.status === 403) return;
      } catch {
        if (controller.signal.aborted) return;
      }
      if (controller.signal.aborted) return;
      if (attempt < 2) {
        timer = setTimeout(() => void publish(attempt + 1), attempt === 0 ? 1000 : 3000);
      } else {
        // A later page hydration retries again; credentials/profile are untouched.
        console.error("Contact update could not be queued after authentication");
      }
    }
    void publish(0);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [userId, anonymous, updatedAt, email, phone]);
}